import type { intlFormatPropTypes } from "react-intl";

import type {
  RecommendedCardData,
  RecommendedItemKey,
} from "../types/model/recommended";
import { formatInstantBookListingTitle } from "../utils/host-hub-instant-book.utils";
import { formatListingRowTitle } from "../utils/host-hub-listing.utils";
import useHostHubIncompleteEventFaqSpaces from "./useHostHubIncompleteEventFaqSpaces";
import useHostHubIncompleteVideoTourListings from "./useHostHubIncompleteVideoTourListings";
import useHostHubInstantBookListings from "./useHostHubInstantBookListings";
import useHostHubUnsyncedCalendarSpaces from "./useHostHubUnsyncedCalendarSpaces";

/**
 * Display order for recommended cards within the section (between the
 * submit-review items above and the read-reviews group below). Cards are
 * sorted by this order so they stay consistent as more eligibility checks land.
 */
const RECOMMENDED_ITEM_ORDER: RecommendedItemKey[] = [
  "enable_instant_book",
  "calendar_sync",
  "event_faq",
  "add_video_tour",
];

/**
 * Eligible "Recommended" nudge cards for the host. Each card is added here as
 * its eligibility check is built out (e.g. listings missing a video tour, an
 * unsynced calendar), so the section stays data-driven rather than rendering a
 * hardcoded list.
 */
const useRecommendedItems = (
  intl: intlFormatPropTypes,
): RecommendedCardData[] => {
  const { listings: instantBookListings } = useHostHubInstantBookListings();
  const { spaces: unsyncedCalendarSpaces } = useHostHubUnsyncedCalendarSpaces();
  const { spaces: incompleteEventFaqSpaces } =
    useHostHubIncompleteEventFaqSpaces();
  const { listings: videoTourListings } =
    useHostHubIncompleteVideoTourListings();

  const cards: RecommendedCardData[] = [];

  if (instantBookListings.length > 0) {
    cards.push({
      key: "enable_instant_book",
      items: instantBookListings.map((listing) => ({
        id: listing.id,
        mediaAlt: listing.mediaAlt,
        mediaUrl: listing.mediaUrl,
        primaryCtaRoute: listing.primaryCtaRoute,
        title: formatInstantBookListingTitle({ intl, listing }),
      })),
    });
  }

  if (unsyncedCalendarSpaces.length > 0) {
    cards.push({
      key: "calendar_sync",
      items: unsyncedCalendarSpaces,
    });
  }

  if (incompleteEventFaqSpaces.length > 0) {
    cards.push({
      key: "event_faq",
      items: incompleteEventFaqSpaces,
    });
  }

  if (videoTourListings.length > 0) {
    cards.push({
      key: "add_video_tour",
      items: videoTourListings.map((listing) => ({
        id: listing.id,
        mediaAlt: listing.mediaAlt,
        mediaUrl: listing.mediaUrl,
        primaryCtaRoute: listing.primaryCtaRoute,
        title: formatListingRowTitle({
          intl,
          spaceTitle: listing.spaceTitle,
          useType: listing.useType,
        }),
      })),
    });
  }

  return cards.sort(
    (firstCard, secondCard) =>
      RECOMMENDED_ITEM_ORDER.indexOf(firstCard.key) -
      RECOMMENDED_ITEM_ORDER.indexOf(secondCard.key),
  );
};

export default useRecommendedItems;
